import { useEffect, useState } from "react";
import {
  UserPreferences,
  getMyPreferences,
  updateMyPreferences,
} from "../data/employeeData";
import { clearPersistedGridState } from "./useSessionState";

/**
 * The signed-in user's portal preferences.
 *
 * These live on the employee record so they follow the user to whatever machine
 * they log in on. They are also mirrored into localStorage, because the list
 * screens need to know whether to restore their filters at the moment they
 * mount — which is well before any API call could come back. The cached copy is
 * the answer used on that first render, and the server response corrects it a
 * moment later if they differ.
 */

const CACHE_KEY = "bamfords.userPreferences";

export const defaultPreferences: UserPreferences = {
  rememberFilters: true,
};

function readCache(): UserPreferences {
  try {
    const stored = localStorage.getItem(CACHE_KEY);
    if (stored === null) return defaultPreferences;
    return { ...defaultPreferences, ...JSON.parse(stored) };
  } catch (error) {
    // Corrupt entry, or storage unavailable (private browsing).
    console.warn("Could not read cached user preferences:", error);
    return defaultPreferences;
  }
}

function writeCache(preferences: UserPreferences): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.warn("Could not cache user preferences:", error);
  }
}

let current: UserPreferences = readCache();

const listeners = new Set<(preferences: UserPreferences) => void>();

function publish(preferences: UserPreferences): void {
  current = preferences;
  writeCache(preferences);
  listeners.forEach((listener) => listener(preferences));
}

/** Synchronous read for code that runs outside React's render cycle. */
export function getPreferences(): UserPreferences {
  return current;
}

/** Whether list screens should keep their filters between sections. */
export function shouldRememberFilters(): boolean {
  return current.rememberFilters;
}

/**
 * Pulls the preferences from the server. Called once the user is logged in;
 * failures are non-fatal and leave the cached values in place.
 */
export async function loadUserPreferences(): Promise<UserPreferences> {
  try {
    const preferences = await getMyPreferences();
    publish(preferences);
    return preferences;
  } catch (error) {
    console.warn("Could not load user preferences:", error);
    return current;
  }
}

/**
 * Saves the preferences and applies them immediately. Turning "remember
 * filters" off also throws away anything already persisted, so the setting
 * takes effect on the next screen rather than after the next login.
 */
export async function saveUserPreferences(
  preferences: UserPreferences
): Promise<UserPreferences> {
  const saved = await updateMyPreferences(preferences);

  if (!saved.rememberFilters) {
    clearPersistedGridState();
  }

  publish(saved);
  return saved;
}

/**
 * Drops the cached preferences back to the defaults. Used at login so one user
 * does not inherit the previous user's settings on a shared machine.
 */
export function clearCachedPreferences(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.warn("Could not clear cached user preferences:", error);
  }
  publish(defaultPreferences);
}

/** Subscribes to preference changes. Returns an unsubscribe function. */
export function subscribeToPreferences(
  listener: (preferences: UserPreferences) => void
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React binding for the preferences above. */
export function useUserPreferences(): UserPreferences {
  const [preferences, setPreferences] = useState<UserPreferences>(current);

  useEffect(() => subscribeToPreferences(setPreferences), []);

  return preferences;
}
